
import { Component, SimpleChanges, computed, effect, inject, Input, OnChanges, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
    ModulesReportsApplicationReportCategoriesReportCategoryResponse,
    ModulesReportsApplicationReportParameterDependenciesReportParameterDependencyResponse,
    ModulesReportsApplicationReportParameterLinksReportParameterLinkResponse,
    ModulesReportsApplicationReportParametersReportParameterResponse,
    ModulesReportsApplicationReportsReportResponse,
    ModulesReportsEndpointsContractsReportsExecuteReportContextRequest
} from '../../api-client';
import { ReportsStore } from './reports-store.service';
import { LoggingService } from '../../services/shared/logging/logging.service';
import { ToastrService } from 'ngx-toastr';
import { ReportExecutionService } from '../../services/reports/report-execution.service';
import { ReportContextOption } from '../../models/report-context-option';

interface ReportContextInput {
    tournamentId?: string | null;
    roundId?: string | null;
    courseId?: string | null;
}

type ReportFormat = 'PDF' | 'TXT' | 'XLS';
type AGroupSortOption = 'Rank' | 'Category' | 'Alpha';

@Component({
    selector: 'app-reports-workspace',
    standalone: true,
    imports: [ReactiveFormsModule],
    templateUrl: './reports-workspace.component.html',
    styleUrls: ['./reports-workspace.component.scss'],
    providers: [ReportsStore]
})
export class ReportsWorkspaceComponent implements OnInit, OnChanges {
    private readonly store = inject(ReportsStore);
    private readonly loggingService = inject(LoggingService);
    private readonly toastr = inject(ToastrService);
    private readonly fb = inject(FormBuilder);
    private readonly reportExecutionService = inject(ReportExecutionService);

    @Input() helpText: string | null = null;
    @Input() roundOptions: ReportContextOption[] | null = null;
    @Input() courseOptions: ReportContextOption[] | null = null;

    private initialContextState: ReportContextInput = {};

    @Input()
    set initialContext(value: ReportContextInput | null) {
        this.initialContextState = value ? value : {};
        this.contextForm.patchValue({
            roundId: this.normalizeOptionId(this.initialContextState.roundId),
            courseId: this.normalizeOptionId(this.initialContextState.courseId)
        });
    }

    readonly loading = this.store.loading;
    readonly error = this.store.error;

    readonly formatOptions: ReportFormat[] = ['PDF', 'TXT', 'XLS'];
    readonly selectedFormat = signal<ReportFormat>('PDF');

    readonly selectedCategoryId = signal<number | null>(null);
    readonly selectedReportId = signal<number | null>(null);
    readonly isExecuting = signal(false);

    readonly categories = computed<ModulesReportsApplicationReportCategoriesReportCategoryResponse[]>(() => {
        const source = this.store.categories();
        return [...source];
    });

    readonly reportsForSelectedCategory = computed<ModulesReportsApplicationReportsReportResponse[]>(() => {
        const categoryId = this.selectedCategoryId();
        const availableReports = this.store.reports();
        const activeReports = availableReports.filter(report => report.isActive !== false);
        const filtered = categoryId === null
            ? activeReports
            : activeReports.filter(report => report.reportCategoryId === categoryId);
        return [...filtered].sort((a, b) => {
            const orderA = typeof a.sortOrder === 'number' ? a.sortOrder : Number.MAX_SAFE_INTEGER;
            const orderB = typeof b.sortOrder === 'number' ? b.sortOrder : Number.MAX_SAFE_INTEGER;
            if (orderA !== orderB) {
                return orderA - orderB;
            }
            return (a.name || '').localeCompare(b.name || '');
        });
    });

    readonly selectedReport = computed<ModulesReportsApplicationReportsReportResponse | null>(() => {
        const reportId = this.selectedReportId();
        if (!reportId) {
            return null;
        }
        return this.store.reports().find(report => report.reportId === reportId) || null;
    });

    readonly aGroupSortOptions: ReadonlyArray<{ value: AGroupSortOption; label: string }> = [
        { value: 'Rank', label: 'A/B/C Rank' },
        { value: 'Category', label: 'Category, then Name' },
        { value: 'Alpha', label: 'Name' }
    ];
    readonly selectedAGroupSort = signal<AGroupSortOption>('Rank');
    readonly showAGroupRank = signal(true);

    private readonly parameterValues = signal<Record<number, string>>({});
    private readonly parameterNames = signal<string[]>([]);
    private parameterIdByName = new Map<string, number>();
    private selectedReportParameterIds: Set<number> = new Set();
    private parameterDependencyMap = new Map<number, number[]>();

    readonly showAGroupParameters = computed(() => {
        const names = this.parameterNames();
        return names.includes('SortBy') || names.includes('ShowRank');
    });

    readonly holeNumberOptions: ReadonlyArray<number> = Array.from({ length: 18 }, (_, index) => index + 1);
    readonly selectedHoleNumber = signal<string>('1');

    readonly showHoleNumberParameter = computed(() => {
        const names = this.parameterNames();
        return names.includes('HoleNumber');
    });

    readonly contextForm: FormGroup = this.fb.group({
        roundId: [''],
        courseId: ['']
    });

    constructor() {
        effect(() => {
            const categories = this.categories();
            if (categories.length === 0) {
                return;
            }

            const currentCategoryId = this.selectedCategoryId();
            if (currentCategoryId !== null && categories.some(category => category.reportCategoryId === currentCategoryId)) {
                return;
            }

            const firstCategory = categories.find(category => typeof category.reportCategoryId === 'number');
            if (firstCategory && typeof firstCategory.reportCategoryId === 'number') {
                this.selectedCategoryId.set(firstCategory.reportCategoryId);
            }
        });

        effect(() => {
            const reports = this.reportsForSelectedCategory();
            const currentReportId = this.selectedReportId();
            if (reports.length === 0) {
                this.selectedReportId.set(null);
                return;
            }

            if (currentReportId !== null && reports.some(report => report.reportId === currentReportId)) {
                return;
            }

            const firstReport = reports.find(report => typeof report.reportId === 'number');
            if (firstReport && typeof firstReport.reportId === 'number') {
                this.selectedReportId.set(firstReport.reportId);
            }
        });

        effect(() => {
            if (this.selectedReportId() !== null) {
                this.selectedFormat.set('PDF');
            }
        });

        effect(() => {
            const report = this.selectedReport();
            const parameters = this.store.parameters();
            const parameterLinks = this.store.parameterLinks();
            const parameterDependencies = this.store.parameterDependencies();
            this.configureParametersForReport(report, parameters, parameterLinks, parameterDependencies);
        });
    }

    ngOnInit(): void {
        this.store.loadCatalog();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['roundOptions']) {
            this.syncContextControl('roundId', this.roundOptions);
        }
        if (changes['courseOptions']) {
            this.syncContextControl('courseId', this.courseOptions);
        }
    }

    get hasContextSelectors(): boolean {
        const roundCount = this.roundOptions ? this.roundOptions.length : 0;
        const courseCount = this.courseOptions ? this.courseOptions.length : 0;
        return roundCount > 0 || courseCount > 0;
    }

    handleCategoryChange(event: Event): void {
        const selectElement = event.target as HTMLSelectElement;
        const value = selectElement ? selectElement.value : '';
        if (!value) {
            const firstCategory = this.categories().find(category => typeof category.reportCategoryId === 'number');
            if (firstCategory && typeof firstCategory.reportCategoryId === 'number') {
                this.selectedCategoryId.set(firstCategory.reportCategoryId);
            }
            return;
        }
        const parsed = Number(value);
        this.selectedCategoryId.set(isNaN(parsed) ? null : parsed);
    }

    selectReport(reportId: number | undefined | null): void {
        if (typeof reportId !== 'number') {
            this.selectedReportId.set(null);
            return;
        }
        this.selectedReportId.set(reportId);
    }

    handleFormatChange(event: Event): void {
        const selectElement = event.target as HTMLSelectElement;
        const value = (selectElement ? selectElement.value : 'PDF') as ReportFormat;
        if (this.formatOptions.includes(value)) {
            this.selectedFormat.set(value);
            return;
        }

        this.selectedFormat.set('PDF');
    }

    handleAGroupSortChange(option: AGroupSortOption): void {
        this.setParameterValueByName('SortBy', option);
        this.selectedAGroupSort.set(option);
    }

    handleAGroupShowRankChange(event: Event): void {
        const checkbox = event.target as HTMLInputElement;
        const isChecked = !!checkbox?.checked;
        this.showAGroupRank.set(isChecked);
        this.setParameterValueByName('ShowRank', isChecked ? 'True' : 'False');
    }

    async runReport(): Promise<void> {
        const reportId = this.selectedReportId();
        if (!reportId) {
            this.toastr.error('Please select a report to run.', 'Report Required');
            return;
        }

        const format = this.selectedFormat();
        const contextPayload = this.buildContextPayload();
        const parameterPayload = this.buildParameterPayload();

        if (this.showHoleNumberParameter() && !parameterPayload['HoleNumber']) {
            this.toastr.error('Please choose a hole number before running the report.', 'Hole Number Required');
            return;
        }

        this.isExecuting.set(true);

        try {
            const normalizedContext = this.isContextEmpty(contextPayload)
                ? undefined
                : ({ ...contextPayload } as Record<string, string | number | boolean | null | undefined>);

            await this.reportExecutionService.executeAndDownload({
                reportId,
                format,
                context: normalizedContext,
                parameters: Object.keys(parameterPayload).length === 0 ? undefined : parameterPayload,
                successMessage: `Downloaded ${this.selectedReport()?.name || 'report'}`
            });
        } catch (error: any) {
            this.loggingService.error('ReportsWorkspaceComponent', 'Report execution failed', error);
            // Errors are surfaced by the execution service; no additional toast needed here.
        } finally {
            this.isExecuting.set(false);
        }
    }

    private buildContextPayload(): ModulesReportsEndpointsContractsReportsExecuteReportContextRequest {
        const raw = this.contextForm.value as ReportContextInput;
        const context: ModulesReportsEndpointsContractsReportsExecuteReportContextRequest = {};
        if (this.initialContextState.tournamentId) {
            context.tournamentId = this.initialContextState.tournamentId;
        }
        if (raw.roundId) {
            context.roundId = raw.roundId;
        }
        if (raw.courseId) {
            context.courseId = raw.courseId;
        }
        return context;
    }

    private isContextEmpty(context: ModulesReportsEndpointsContractsReportsExecuteReportContextRequest): boolean {
        return !context.tournamentId && !context.roundId && !context.courseId;
    }

    private buildParameterPayload(): Record<string, string> {
        if (this.selectedReportParameterIds.size === 0) {
            return {};
        }

        const parameterValues = this.parameterValues();
        const payload: Record<string, string> = {};

        this.store
            .parameters()
            .forEach(parameter => {
                const parameterId = parameter.reportParameterId;
                if (!parameterId || !this.selectedReportParameterIds.has(parameterId)) {
                    return;
                }

                const name = parameter.name;
                const value = parameterValues[parameterId];
                if (typeof name === 'string' && name.length > 0 && typeof value === 'string' && value.length > 0) {
                    payload[name] = value;
                }
            });

        return payload;
    }

    private syncContextControl(controlName: 'roundId' | 'courseId', options: ReportContextOption[] | null): void {
        const control = this.contextForm.get(controlName);
        if (!control) {
            return;
        }

        const optionList = options ?? [];
        const normalizedOptions = optionList.map(option => ({ ...option, id: this.normalizeOptionId(option.id) }));
        const desiredValue = controlName === 'roundId'
            ? this.normalizeOptionId(this.initialContextState.roundId)
            : this.normalizeOptionId(this.initialContextState.courseId);
        const currentValue = this.normalizeOptionId(control.value as string | null);

        if (normalizedOptions.length === 0) {
            if (currentValue) {
                control.setValue('', { emitEvent: false });
            }
            return;
        }

        if (desiredValue && normalizedOptions.some(option => option.id === desiredValue)) {
            if (currentValue !== desiredValue) {
                control.setValue(desiredValue, { emitEvent: false });
            }
            return;
        }

        const hasMatch = currentValue && normalizedOptions.some(option => option.id === currentValue);
        if (!hasMatch) {
            control.setValue(normalizedOptions[0]?.id ?? '', { emitEvent: false });
        }
    }

    private configureParametersForReport(
        report: ModulesReportsApplicationReportsReportResponse | null,
        parameters: ModulesReportsApplicationReportParametersReportParameterResponse[],
        parameterLinks: ModulesReportsApplicationReportParameterLinksReportParameterLinkResponse[],
        parameterDependencies: ModulesReportsApplicationReportParameterDependenciesReportParameterDependencyResponse[]
    ): void {
        this.parameterIdByName = new Map();
        this.selectedReportParameterIds = new Set();
        this.parameterDependencyMap = new Map();
        this.parameterNames.set([]);
        this.parameterValues.set({});

        if (!report?.reportId) {
            this.syncParameterSignalsFromValues();
            return;
        }

        const parameterMap = new Map<number, ModulesReportsApplicationReportParametersReportParameterResponse>();
        parameters.forEach(parameter => {
            if (typeof parameter.reportParameterId === 'number') {
                parameterMap.set(parameter.reportParameterId, parameter);
            }
        });

        const linkedParameterIds: number[] = [];
        parameterLinks
            .filter(link => link.reportId === report.reportId)
            .forEach(link => {
                if (typeof link.reportParameterId !== 'number') {
                    return;
                }

                const parameter = parameterMap.get(link.reportParameterId);
                if (!parameter) {
                    return;
                }

                linkedParameterIds.push(link.reportParameterId);
                if (typeof parameter.name === 'string' && parameter.name.length > 0) {
                    this.parameterIdByName.set(parameter.name, link.reportParameterId);
                }
            });

        this.selectedReportParameterIds = new Set(linkedParameterIds);

        parameterDependencies.forEach(dependency => {
            const parentId = dependency.parentParameterId;
            const childId = dependency.childParameterId;

            if (
                typeof parentId !== 'number' ||
                typeof childId !== 'number' ||
                !this.selectedReportParameterIds.has(parentId) ||
                !this.selectedReportParameterIds.has(childId)
            ) {
                return;
            }

            const children = this.parameterDependencyMap.get(parentId) ?? [];
            children.push(childId);
            this.parameterDependencyMap.set(parentId, children);
        });

        this.parameterNames.set([...this.parameterIdByName.keys()]);

        if (this.parameterIdByName.has('SortBy')) {
            this.setParameterValueByName('SortBy', 'Rank');
        }

        if (this.parameterIdByName.has('ShowRank')) {
            this.setParameterValueByName('ShowRank', 'True');
        }

        if (this.parameterIdByName.has('HoleNumber')) {
            this.setParameterValueByName('HoleNumber', '1');
        }

        this.syncParameterSignalsFromValues();
    }

    private setParameterValueByName(name: string, value: string): void {
        const parameterId = this.parameterIdByName.get(name);
        if (typeof parameterId !== 'number') {
            return;
        }

        this.parameterValues.update(current => {
            const next = { ...current };
            this.applyValueRecursive(next, parameterId, value, new Set());
            return next;
        });
    }

    private getParameterValueByName(name: string): string | undefined {
        const parameterId = this.parameterIdByName.get(name);
        if (typeof parameterId !== 'number') {
            return undefined;
        }

        const values = this.parameterValues();
        return values[parameterId];
    }

    private applyValueRecursive(target: Record<number, string>, parameterId: number, value: string, visited: Set<number>): void {
        if (visited.has(parameterId)) {
            return;
        }

        visited.add(parameterId);
        target[parameterId] = value;

        const children = this.parameterDependencyMap.get(parameterId);
        if (!children || children.length === 0) {
            return;
        }

        children.forEach(childId => this.applyValueRecursive(target, childId, value, visited));
    }

    private syncParameterSignalsFromValues(): void {
        const sortValue = this.getParameterValueByName('SortBy');
        if (sortValue === 'Rank' || sortValue === 'Category' || sortValue === 'Alpha') {
            this.selectedAGroupSort.set(sortValue as AGroupSortOption);
        } else {
            this.selectedAGroupSort.set('Rank');
        }

        const showRankValue = this.getParameterValueByName('ShowRank');
        if (typeof showRankValue === 'string') {
            this.showAGroupRank.set(showRankValue.toLowerCase() !== 'false');
        } else {
            this.showAGroupRank.set(true);
        }

        const holeNumberValue = this.getParameterValueByName('HoleNumber');
        if (typeof holeNumberValue === 'string' && this.isValidHoleNumber(holeNumberValue)) {
            this.selectedHoleNumber.set(holeNumberValue);
        } else {
            this.selectedHoleNumber.set('1');
        }
    }

    private normalizeOptionId(value: string | null | undefined): string {
        if (value === null || value === undefined) {
            return '';
        }

        return value.toString();
    }

    handleHoleNumberChange(event: Event): void {
        const element = event.target as HTMLSelectElement;
        const rawValue = element?.value ?? '';
        const normalized = rawValue.trim();

        if (this.isValidHoleNumber(normalized)) {
            this.selectedHoleNumber.set(normalized);
            this.setParameterValueByName('HoleNumber', normalized);
            return;
        }

        this.selectedHoleNumber.set('1');
        this.setParameterValueByName('HoleNumber', '1');
    }

    private isValidHoleNumber(value: string): boolean {
        if (!value) {
            return false;
        }

        const parsed = Number(value);
        return Number.isInteger(parsed) && parsed > 0 && parsed <= 18;
    }
}
